import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import ScatterBackground from "@/components/ScatterBackground";

const BUY_URL =
  "https://dyorswap.org/launchInfo/?id=0x80c0e0d2e9985456475bE538bA5ddaB24A920000&chainId=5042";
const X_URL = "https://x.com/CATBATHATRATFAT";

const HERO_LINES = ["USDC", "COIN", "OF", "MEMES"];

export default function Hero() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end start"],
  });

  // Parallax for the central batrat
  const yBat = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const rotBat = useTransform(scrollYProgress, [0, 1], [0, -18]);
  const scaleBat = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  // 3D tilt on mouse move
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const tiltX = useSpring(tilt.x, { stiffness: 90, damping: 14 });
  const tiltY = useSpring(tilt.y, { stiffness: 90, damping: 14 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const nx = (e.clientX - cx) / (r.width / 2);
    const ny = (e.clientY - cy) / (r.height / 2);
    setTilt({ x: -ny * 10, y: nx * 14 });
  };

  useEffect(() => {
    tiltX.set(tilt.x);
    tiltY.set(tilt.y);
  }, [tilt, tiltX, tiltY]);

  return (
    <section
      ref={wrapRef}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className="relative min-h-[100svh] w-full overflow-hidden"
      style={{ backgroundColor: "var(--cream)" }}
      data-testid="hero-section"
    >
      {/* halftone strip top */}
      <div className="absolute inset-x-0 top-0 h-16 halftone opacity-40 z-[3]" />

      {/* scattered images */}
      <ScatterBackground seed={11} count={38} />

      {/* Nav bar */}
      <div className="relative z-20 flex items-center justify-between px-6 md:px-10 pt-6">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-full bg-[color:var(--usdc-blue)] border-2 border-[color:var(--ink)] btn-shadow flex items-center justify-center overflow-hidden"
            data-testid="brand-logo"
          >
            <img
              src="/img/batrat.jpg"
              alt="USDC logo"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-2xl md:text-3xl tracking-tight">
              USDC
            </span>
            <span
              className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink)]/75 mt-1"
              data-testid="brand-tagline"
            >
              FatCatBatRatWifHat
            </span>
          </div>
          <span className="hidden md:inline-block font-mono text-[10px] uppercase border border-[color:var(--ink)] px-2 py-1">
            ticker: $USDC
          </span>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-x-link"
            className="font-mono text-xs uppercase tracking-widest border-2 border-[color:var(--ink)] bg-white px-3 py-2 btn-shadow"
          >
            follow / X
          </a>
          <a
            href={BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-buy-link"
            className="font-mono text-xs uppercase tracking-widest border-2 border-[color:var(--ink)] bg-[color:var(--meme-yellow)] px-3 py-2 btn-shadow"
          >
            buy $usdc
          </a>
        </div>
      </div>

      {/* Big kinetic hero copy */}
      <div className="relative z-10 mt-10 md:mt-14 px-6 md:px-10">
        <div className="max-w-[1500px] mx-auto">
          {/* Chapter tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="font-editorial italic text-lg md:text-xl">
              00 —
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
              a real fake dollar / on-chain rodent
            </span>
          </motion.div>

          {/* Masked line reveal */}
          <h1
            className="font-display uppercase text-[color:var(--ink)] leading-[0.82]"
            data-testid="hero-title"
          >
            {HERO_LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%", rotate: i % 2 === 0 ? -2 : 3 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{
                    duration: 1.1,
                    delay: 0.15 + i * 0.13,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="inline-block text-[18vw] md:text-[15vw] lg:text-[13.5vw]"
                  style={{
                    color:
                      i === 2
                        ? "transparent"
                        : i === 1
                        ? "var(--usdc-blue)"
                        : i === 3
                        ? "var(--meme-pink)"
                        : "var(--ink)",
                    WebkitTextStroke:
                      i === 2 ? "3px var(--ink)" : "0",
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          {/* Sub row: batrat + description */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* central batrat with parallax + tilt */}
            <motion.div
              style={{ y: yBat, rotate: rotBat, scale: scaleBat }}
              className="md:col-span-6 relative flex items-center justify-center"
            >
              <motion.div
                style={{
                  rotateX: tiltX,
                  rotateY: tiltY,
                  transformPerspective: 900,
                }}
                className="relative"
              >
                {/* blue spotlight disc */}
                <div
                  className="absolute -inset-6 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 40%, rgba(30,82,241,0.45), rgba(30,82,241,0) 65%)",
                    filter: "blur(10px)",
                    zIndex: -1,
                  }}
                />
                {/* concentric halftone */}
                <div
                  className="absolute -inset-10 rounded-full halftone opacity-40"
                  style={{ zIndex: -1 }}
                />
                <motion.img
                  src="/img/batrat.jpg"
                  alt="BAT RAT HAT RAT FAT — USDC mascot"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.2, delay: 0.4 }}
                  className="w-[70vw] max-w-[520px] h-auto float-y select-none"
                  data-testid="hero-mascot"
                  style={{
                    mixBlendMode: "multiply",
                  }}
                />
                {/* stamp */}
                <div
                  className="absolute -right-6 -top-2 md:right-2 md:-top-4 z-10 pulse-stamp"
                  style={{ color: "var(--meme-pink)" }}
                >
                  <span className="stamp font-display text-xl md:text-2xl bg-[color:var(--cream)]">
                    NOT FINANCIAL ADVICE
                  </span>
                </div>
                <div
                  className="absolute -left-4 bottom-4 md:-left-8 md:bottom-8 z-10"
                  style={{ color: "var(--usdc-blue)" }}
                >
                  <span
                    className="stamp font-display text-lg md:text-xl bg-[color:var(--meme-yellow)]"
                    style={{ transform: "rotate(6deg)" }}
                  >
                    CHAIN · ARC
                  </span>
                </div>
              </motion.div>
            </motion.div>

            {/* CTA copy block */}
            <div className="md:col-span-6 md:pl-8 relative">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="font-editorial italic text-2xl md:text-3xl leading-[1.15] max-w-[42ch]"
              >
                A rat with bat wings and a beanie printed{" "}
                <span className="not-italic font-display text-[color:var(--usdc-blue)]">
                  ONE DOLLAR
                </span>{" "}
                on itself and called it stable.
                <br />
                We&apos;re calling it&nbsp;
                <span
                  className="not-italic font-display"
                  style={{
                    backgroundColor: "var(--meme-yellow)",
                    padding: "0 0.15em",
                  }}
                >
                  culture
                </span>
                .
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <a
                  href={BUY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-buy-button"
                  className="group inline-flex items-center gap-3 bg-[color:var(--usdc-blue)] text-white font-display uppercase tracking-tight text-xl md:text-2xl px-6 py-4 border-2 border-[color:var(--ink)] btn-shadow"
                >
                  <span>BUY $USDC</span>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="square"
                    className="transition-transform group-hover:translate-x-1"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </a>
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-x-button"
                  className="group inline-flex items-center gap-3 bg-[color:var(--ink)] text-[color:var(--cream)] font-display uppercase tracking-tight text-xl md:text-2xl px-6 py-4 border-2 border-[color:var(--ink)] btn-shadow"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.244 2H21l-6.55 7.49L22 22h-6.828l-4.79-6.264L4.8 22H2l7.02-8.028L2 2h6.914l4.343 5.75L18.244 2Zm-1.196 18h1.635L7.05 3.9H5.31L17.048 20Z" />
                  </svg>
                  <span>@CATBATHATRATFAT</span>
                </a>

                <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--ink)]/70 border-l-2 border-[color:var(--ink)] pl-3">
                  contract
                  <br />
                  <span className="text-[color:var(--ink)]">
                    0x80c0…0000
                  </span>
                </div>
              </motion.div>

              {/* stats mini strip */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.1 }}
                className="mt-10 grid grid-cols-3 gap-3 max-w-[520px]"
              >
                {[
                  ["supply", "1,000,000,000"],
                  ["tax", "0 / 0"],
                  ["chain", "ARC"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="border-2 border-[color:var(--ink)] bg-white p-3"
                    data-testid={`stat-${k}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink)]/60">
                      {k}
                    </div>
                    <div className="font-display text-lg md:text-xl mt-1 leading-none">
                      {v}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em]">
          scroll / squeak
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-[2px] h-8 bg-[color:var(--ink)]"
        />
      </motion.div>
    </section>
  );
}
